import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Link2,
  Unlink,
  CheckCircle,
  AlertCircle,
  Clock,
  Loader2,
  Copy,
  Info,
  MessageSquare,
  Phone,
  Search
} from 'lucide-react';
import {
  Alert,
  AlertDescription
} from '@/components/ui/alert';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { supabase } from '@/utils/supabase';
import { createComponentLogger } from '@/utils/logger';
import { cn } from '@/lib/utils';
import { SmsService, type PooledNumber, type TwilioSearchResult } from '@/services/smsService';
import {
  LwButton,
  LwModal,
  LwStatusPill
} from '@/components/lw';

const log = createComponentLogger('TwilioSmsCredentials', 'integration');

interface TwilioSmsCredentialsProps {
  userId: string;
  userEmail: string;
  firstName?: string;
  lastName?: string;
  isAdminView?: boolean;
  canManageNumbers?: boolean;
  canSync?: boolean;
  targetUserId?: string;
  presentation?: 'default' | 'profile';
}

export const TwilioSmsCredentials: React.FC<TwilioSmsCredentialsProps> = ({
  userId,
  isAdminView = false,
  canManageNumbers = isAdminView,
  canSync = isAdminView,
  targetUserId,
  presentation = 'default'
}) => {
  const isProfilePresentation = presentation === 'profile';
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(false);
  const [searching, setSearching] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [connectDialogOpen, setConnectDialogOpen] = useState(false);
  const [unlinkDialogOpen, setUnlinkDialogOpen] = useState(false);

  // Wizard flow state
  const [assignmentFlow, setAssignmentFlow] = useState<'initial' | 'pool' | 'purchase'>('initial');

  // Search state
  const [pooledNumbers, setPooledNumbers] = useState<PooledNumber[]>([]);
  const [twilioSearchResults, setTwilioSearchResults] = useState<TwilioSearchResult[]>([]);
  const [selectedNumberId, setSelectedNumberId] = useState<string>('');
  const [selectedTwilioNumber, setSelectedTwilioNumber] = useState<string>('');
  const [areaCodeFilter, setAreaCodeFilter] = useState<string>('');

  // Twilio SMS state
  const [smsNumber, setSmsNumber] = useState<string | null>(null);
  const [twilioSid, setTwilioSid] = useState<string | null>(null);
  const [numberStatus, setNumberStatus] = useState<'active' | 'pending' | 'released' | 'failed' | null>(null);
  const [provisionedAt, setProvisionedAt] = useState<Date | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);

  // Load Twilio SMS status
  useEffect(() => {
    loadTwilioStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload Twilio status only when target user changes
  }, [userId]);

  const loadTwilioStatus = async () => {
    try {
      setLoading(true);

      const { data, error } = await supabase
        .from('users')
        .select('business_sms_number, twilio_phone_sid, twilio_number_status, twilio_provisioned_at')
        .eq('id', userId)
        .single();

      if (error) {
        log.error('Failed to load Twilio SMS status', { userId }, error);
        return;
      }

      if (data) {
        setSmsNumber(data.business_sms_number);
        setTwilioSid(data.twilio_phone_sid);
        setNumberStatus(data.twilio_number_status);
        setProvisionedAt(data.twilio_provisioned_at ? new Date(data.twilio_provisioned_at) : null);
      }
    } catch (error) {
      log.error('Error loading Twilio SMS status', { userId }, error as Error);
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableNumbers = async (forceRefresh: boolean = false) => {
    try {
      setSearching(true);

      log.info('Loading available numbers from pool', {
        userId,
        areaCodeFilter,
        forceRefresh
      });

      const result = await SmsService.listAvailableNumbers({
        areaCode: areaCodeFilter || undefined,
        searchTwilio: false,  // NEVER auto-search Twilio (sync handles it)
        limit: 20,
        targetUserId: targetUserId || userId
      });

      if (!result.success) {
        throw new Error(result.error || 'Failed to load numbers from pool');
      }

      setPooledNumbers(result.pooledNumbers || []);

      log.info('Loaded available numbers', {
        count: result.pooledNumbers?.length || 0,
        poolSummary: result.poolSummary
      });

      // No auto-searching Twilio anymore - user sees "No numbers" state instead

    } catch (error) {
      log.error('Failed to load available numbers', { userId }, error as Error);
      toast.error('Failed to load available numbers from pool');
      setPooledNumbers([]);
    } finally {
      setSearching(false);
    }
  };

  const searchTwilioInventory = async () => {
    try {
      setSearching(true);
      setTwilioSearchResults([]);

      log.info('Searching Twilio inventory for purchase', {
        areaCodeFilter,
        userId
      });

      const result = await SmsService.listAvailableNumbers({
        areaCode: areaCodeFilter || undefined,
        searchTwilio: true,  // Only search Twilio when user wants to purchase
        limit: 20,
        targetUserId: targetUserId || userId
      });

      if (!result.success) {
        throw new Error(result.error || 'Failed to search Twilio inventory');
      }

      if (!result.twilioSearchResults || result.twilioSearchResults.length === 0) {
        toast.warning(
          areaCodeFilter
            ? `No numbers available in area code ${areaCodeFilter}. Try a different area code.`
            : 'No numbers available. Try specifying an area code.'
        );
        setTwilioSearchResults([]);
        return;
      }

      setTwilioSearchResults(result.twilioSearchResults);
      toast.success(`Found ${result.twilioSearchResults.length} number(s) available for purchase`);

      log.info('Twilio inventory search completed', {
        count: result.twilioSearchResults.length
      });

    } catch (error) {
      log.error('Failed to search Twilio inventory', { userId }, error as Error);
      toast.error('Failed to search Twilio for available numbers');
      setTwilioSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const handleProvisionNumber = async () => {
    try {
      setAssigning(true);
      setSyncError(null);

      if (!selectedTwilioNumber) {
        toast.error('Please select a number to provision');
        return;
      }

      toast.info(`Provisioning number: ${selectedTwilioNumber}...`);

      const result = await SmsService.provisionNumber({
        specificNumber: selectedTwilioNumber,
        areaCode: areaCodeFilter || undefined,
        targetUserId: targetUserId || userId
      });

      if (!result.success) {
        throw new Error(result.error || 'Failed to provision number');
      }

      let assignedPhoneNumber = result.phoneNumber || null;
      let assignedTwilioSid = result.twilioSid || null;

      if (isAdminView) {
        toast.success(`Number ${result.phoneNumber} added to pool! Now assigning to user...`);

        const assignResult = await SmsService.assignNumber({
          userId,
          phoneNumber: result.phoneNumber!
        });

        if (!assignResult.success) {
          throw new Error(assignResult.error || 'Number provisioned but assignment failed');
        }

        assignedPhoneNumber = assignResult.phoneNumber || null;
        assignedTwilioSid = assignResult.twilioSid || null;
      }

      toast.success(`SMS number assigned successfully: ${assignedPhoneNumber}`);
      setSmsNumber(assignedPhoneNumber);
      setTwilioSid(assignedTwilioSid);
      setNumberStatus('active');
      setProvisionedAt(new Date());
      setConnectDialogOpen(false);

      // Don't reload - trust the edge function response to avoid race condition
      // (Database replication lag can cause loadTwilioStatus to return stale data)

    } catch (error) {
      log.error('Failed to provision and assign SMS number', { userId }, error as Error);
      const message = error instanceof Error ? error.message : 'Failed to provision and assign SMS number';
      toast.error(message);
      setSyncError(message);
    } finally {
      setAssigning(false);
    }
  };

  const handleAssignNumber = async () => {
    try {
      setAssigning(true);
      setSyncError(null);

      if (!selectedNumberId) {
        toast.error('Please select a number from the pool');
        return;
      }

      const selectedPool = pooledNumbers.find(n => n.id === selectedNumberId);
      if (!selectedPool) {
        toast.error('Selected number not found');
        return;
      }

      toast.info(`Assigning number from pool: ${selectedPool.phoneNumber}...`);

      const result = await SmsService.assignNumber({
        userId,
        phoneNumber: selectedPool.phoneNumber
      });

      if (!result.success) {
        throw new Error(result.error || 'Failed to assign number');
      }

      toast.success(`SMS number assigned successfully: ${result.phoneNumber}`);
      setSmsNumber(result.phoneNumber || null);
      setTwilioSid(result.twilioSid || null);
      setNumberStatus('active');
      setProvisionedAt(new Date());
      setConnectDialogOpen(false);

      // Don't reload - trust the edge function response to avoid race condition
      // (Database replication lag can cause loadTwilioStatus to return stale data)

    } catch (error) {
      log.error('Failed to assign SMS number', { userId }, error as Error);
      const message = error instanceof Error ? error.message : 'Failed to assign SMS number';
      toast.error(message);
      setSyncError(message);
    } finally {
      setAssigning(false);
    }
  };

  const handleUnassignNumber = async () => {
    try {
      setAssigning(true);

      const result = await SmsService.unassignNumber(userId, 'User requested unassignment');

      if (!result.success) {
        throw new Error(result.error || 'Failed to unassign number');
      }

      toast.success(result.message || 'Number returned to pool successfully');
      setSmsNumber(null);
      setTwilioSid(null);
      setNumberStatus(null);
      setProvisionedAt(null);
      setSyncError(null);
      setUnlinkDialogOpen(false);

    } catch (error) {
      log.error('Failed to unassign SMS number', { userId }, error as Error);
      toast.error('Failed to unassign number');
    } finally {
      setAssigning(false);
    }
  };

  const copyPhoneNumber = () => {
    if (smsNumber) {
      navigator.clipboard.writeText(smsNumber);
      toast.success('Phone number copied to clipboard');
    }
  };

  const getSyncStatusBadge = () => {
    switch (numberStatus) {
      case 'active':
        return <Badge variant="outline" className={cn('bg-green-50 text-green-700 border-green-200', isProfilePresentation && 'lw-credential-card__status')}>
          <CheckCircle className="w-3 h-3 mr-1" />
          Active
        </Badge>;
      case 'failed':
        return <Badge variant="outline" className={cn('bg-red-50 text-red-700 border-red-200', isProfilePresentation && 'lw-credential-card__status')}>
          <AlertCircle className="w-3 h-3 mr-1" />
          Error
        </Badge>;
      case 'pending':
        return <Badge variant="outline" className={cn('bg-yellow-50 text-yellow-700 border-yellow-200', isProfilePresentation && 'lw-credential-card__status')}>
          <Clock className="w-3 h-3 mr-1" />
          Pending
        </Badge>;
      default:
        return <Badge variant="outline" className={cn('bg-gray-50 text-gray-700 border-gray-200', isProfilePresentation && 'lw-credential-card__status')}>
          <AlertCircle className="w-3 h-3 mr-1" />
          Not Assigned
        </Badge>;
    }
  };

  const openConnectDialog = () => {
    if (!canManageNumbers) {
      toast.error('You do not have permission to manage SMS numbers for this user');
      return;
    }

    // Reset all state
    setAssignmentFlow('initial');
    setSelectedNumberId('');
    setSelectedTwilioNumber('');
    setPooledNumbers([]);
    setTwilioSearchResults([]);
    setAreaCodeFilter('');
    setSyncError(null);
    setSearching(false);
    setSyncing(false);

    // Open dialog to initial choice screen
    setConnectDialogOpen(true);

    log.info('Opened number assignment dialog', { userId });
  };

  const handleSelectPool = () => {
    log.info('User selected pool assignment path', { userId });
    setAssignmentFlow('pool');
    setSelectedNumberId('');
    setSelectedTwilioNumber('');
    setTwilioSearchResults([]);
  };

  const handleSelectPurchase = () => {
    log.info('User selected purchase path', { userId });
    setAssignmentFlow('purchase');
    setSelectedNumberId('');
    setSelectedTwilioNumber('');
    setPooledNumbers([]);
  };

  const handleBackToInitial = () => {
    log.info('User returned to initial choice screen', { userId });
    setAssignmentFlow('initial');
    setSelectedNumberId('');
    setSelectedTwilioNumber('');
    setAreaCodeFilter('');
  };

  const handleSyncNumbers = async () => {
    try {
      setSyncing(true);
      setSyncError(null);

      log.info('Manual sync triggered by user', { userId });

      // Step 1: Sync owned Twilio numbers to pool
      const syncResult = await SmsService.syncTwilioNumbers();

      if (!syncResult.success) {
        log.warn('Twilio sync failed', { error: syncResult.error });
        toast.warning('Could not sync from Twilio. Showing cached pool numbers.');
      } else {
        if (syncResult.synced > 0) {
          toast.success(`Imported ${syncResult.synced} number(s) from your Twilio account`);
        } else if (syncResult.updated > 0) {
          toast.info(`Updated ${syncResult.updated} number(s) from Twilio`);
        } else {
          toast.info('Pool is up to date with your Twilio account');
        }
        log.info('Twilio sync completed', {
          synced: syncResult.synced,
          updated: syncResult.updated,
          total: syncResult.total
        });
      }

      // Step 2: Load pool numbers
      await loadAvailableNumbers(false);

    } catch (error) {
      log.error('Failed to sync Twilio numbers', { userId }, error as Error);
      setSyncError('Could not sync from Twilio. Please check your credentials.');
      toast.error('Failed to sync from Twilio');
    } finally {
      setSyncing(false);
    }
  };

  const formatPhoneNumber = (phone: string) => {
    // Format +15551234567 to +1 (555) 123-4567
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    }
    return phone;
  };

  if (loading) {
    return (
      <div className={cn('border rounded-lg p-6 flex items-center justify-center', isProfilePresentation && 'lw-credential-card lw-credential-card--loading')}>
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <>
      <div className={cn('border rounded-lg p-6 space-y-4', isProfilePresentation && 'lw-credential-card')} onClick={(e) => e.stopPropagation()}>
        <div className="lw-credential-card__header flex items-start justify-between">
          <div className="w-full space-y-1">
            <div className="flex items-center justify-between gap-3">
              <h3 className="lw-credential-card__title text-lg font-semibold flex items-center gap-2">
                {isProfilePresentation ? 'Twilio SMS' : 'Twilio SMS Integration'}
                {!isProfilePresentation && getSyncStatusBadge()}
              </h3>
              {isProfilePresentation && getSyncStatusBadge()}
            </div>
            <p className="lw-credential-card__note text-sm text-muted-foreground">
              {isProfilePresentation
                ? (canManageNumbers
                    ? 'Visible to everyone · Pool-assign or purchase new — admin & partner-delegated only'
                    : 'Visible to everyone · Managed by your admin')
                : 'Assign a dedicated phone number for sending SMS messages to clients.'}
            </p>
          </div>
        </div>

        {/* Status Display */}
        {smsNumber ? (
          <div className="space-y-4">
            <Card className={cn('bg-green-50 border-green-200', isProfilePresentation && 'lw-credential-card__status-panel')}>
              <CardContent className={cn('pt-6', isProfilePresentation && 'lw-credential-card__status-content')}>
                <div className={cn('flex items-start justify-between mb-4', isProfilePresentation && 'lw-credential-card__state-heading')}>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span className="font-medium text-green-900">SMS Enabled</span>
                  </div>
                </div>

                <div className="lw-credential-card__kv-list space-y-3">
                  <div className="lw-credential-card__kv-row flex items-center justify-between">
                    <Label className="text-green-800">SMS Phone Number</Label>
                    <div className="flex items-center gap-2">
                      <code className="lw-credential-card__mono px-3 py-1.5 bg-white rounded text-sm font-mono text-green-900">
                        {formatPhoneNumber(smsNumber)}
                      </code>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label="Copy SMS phone number"
                        onClick={copyPhoneNumber}
                        className="h-8 w-8 hover:bg-green-100"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {twilioSid && (
                    <div className="lw-credential-card__kv-row flex items-center justify-between text-sm">
                      <span className="text-green-800">Twilio SID</span>
                      <span className="lw-credential-card__mono text-green-900 text-xs" title={twilioSid}>
                        {twilioSid}
                      </span>
                    </div>
                  )}

                  {provisionedAt && (
                    <div className="lw-credential-card__kv-row flex items-center justify-between text-sm">
                      <span className="text-green-800">Assigned</span>
                      <span className="text-green-900">{provisionedAt.toLocaleDateString()}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {canManageNumbers && (
              <div className="lw-credential-card__actions flex items-center gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setUnlinkDialogOpen(true)}
                  disabled={assigning}
                  className={cn(
                    'gap-2 text-red-600 hover:text-red-700 hover:bg-red-50',
                    isProfilePresentation && 'lw-credential-card__action--danger'
                  )}
                >
                  <Unlink className="h-4 w-4" />
                  {isProfilePresentation ? 'Unassign number…' : 'Unassign Number'}
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {syncError && (
              <Alert variant="destructive" className="bg-red-50 border-red-200">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="text-sm">
                  {syncError}
                </AlertDescription>
              </Alert>
            )}

            <Card className={cn('bg-gray-50 border-gray-200', isProfilePresentation && 'lw-credential-card__empty-panel')}>
              <CardContent className="pt-6">
                <div className="flex flex-col items-center text-center space-y-4">
                  <div className="p-3 bg-gray-100 rounded-full">
                    <MessageSquare className="h-8 w-8 text-gray-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900 mb-1">No SMS Number Assigned</h4>
                    <p className="text-sm text-gray-600 max-w-sm">
                      Assign a Twilio phone number to enable SMS messaging for pre-approvals and client communication.
                    </p>
                  </div>
                  {canManageNumbers && (
                    <Button
                      type="button"
                      onClick={openConnectDialog}
                      disabled={assigning}
                      className="gap-2"
                      size="lg"
                    >
                      <Phone className="h-5 w-5" />
                      {isProfilePresentation ? 'Assign SMS number…' : 'Assign SMS Number'}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {connectDialogOpen && (
        <LwModal
          className="lw-credential-dialog lw-credential-dialog--twilio"
          containerClassName="lw-credential-modal"
          size="wide"
          title={
            assignmentFlow === 'initial'
              ? 'Connect Twilio SMS Number'
              : assignmentFlow === 'pool'
                ? 'Assign from Available Pool'
                : 'Purchase New Number'
          }
          sub={
            assignmentFlow === 'initial'
              ? 'Choose how you’d like to get your business SMS number.'
              : undefined
          }
          onClose={() => setConnectDialogOpen(false)}
          footer={(
            <>
              <LwButton
                variant="sm"
                onClick={() => setConnectDialogOpen(false)}
                disabled={assigning}
              >
                Cancel
              </LwButton>
              {assignmentFlow !== 'initial' && (
                <LwButton
                  variant="sm-solid"
                  onClick={
                    assignmentFlow === 'pool' && selectedNumberId
                      ? handleAssignNumber
                      : assignmentFlow === 'purchase' && selectedTwilioNumber
                        ? handleProvisionNumber
                        : undefined
                  }
                  disabled={assigning || (!selectedNumberId && !selectedTwilioNumber)}
                >
                  {assigning ? (
                    <>
                      <Loader2 className="animate-spin" />
                      {assignmentFlow === 'pool' ? 'Assigning...' : 'Purchasing...'}
                    </>
                  ) : assignmentFlow === 'pool' && selectedNumberId ? (
                    <>
                      <Link2 />
                      Assign Number
                    </>
                  ) : assignmentFlow === 'purchase' && selectedTwilioNumber ? (
                    <>
                      <Phone />
                      Purchase &amp; Assign ($1.15/month)
                    </>
                  ) : (
                    'Select a Number'
                  )}
                </LwButton>
              )}
            </>
          )}
        >
          {assignmentFlow === 'initial' && (
            <>
              <div className="lw-credential-dialog__method-grid">
                <button
                  type="button"
                  className="lw-credential-dialog__method"
                  onClick={handleSelectPool}
                >
                  <span className="lw-credential-dialog__method-icon" data-tone="success">
                    <Phone aria-hidden="true" />
                  </span>
                  <span>
                    <strong>Assign from Available Pool</strong>
                    <small>Use an existing number from your Twilio account (no additional cost)</small>
                  </span>
                </button>
                <button
                  type="button"
                  className="lw-credential-dialog__method"
                  onClick={handleSelectPurchase}
                >
                  <span className="lw-credential-dialog__method-icon" data-tone="purchase">
                    <Search aria-hidden="true" />
                  </span>
                  <span>
                    <strong>Purchase New Number</strong>
                    <small>Search and buy a new number by area code (~$1–2/month)</small>
                  </span>
                </button>
              </div>
              <div className="lw-credential-dialog__notice" data-tone="info">
                <Info aria-hidden="true" />
                <p>
                  Numbers from the pool are already owned and cost nothing extra.
                  Purchasing a new number adds ~$1–2/month to your Twilio bill.
                </p>
              </div>
            </>
          )}

          {assignmentFlow === 'pool' && (
            <>
              <button
                type="button"
                className="lw-credential-dialog__back"
                onClick={handleBackToInitial}
              >
                ← Back to Options
              </button>

              <div className="lw-credential-dialog__search lw-credential-dialog__search--twilio">
                <div className="lw-credential-dialog__field">
                  <Label htmlFor="poolAreaCode">Area code filter</Label>
                  <Input
                    id="poolAreaCode"
                    placeholder="e.g., 303, 720, 818"
                    value={areaCodeFilter}
                    onChange={(event) => setAreaCodeFilter(event.target.value.replace(/\D/g, '').slice(0, 3))}
                    maxLength={3}
                  />
                </div>
                {canSync && (
                  <LwButton
                    variant="sm-solid"
                    onClick={handleSyncNumbers}
                    disabled={syncing || searching}
                  >
                    {syncing ? <Loader2 className="animate-spin" /> : <Phone />}
                    {syncing ? 'Syncing...' : 'Sync from Twilio'}
                  </LwButton>
                )}
                {areaCodeFilter && (
                  <LwButton
                    variant="sm"
                    onClick={() => loadAvailableNumbers(false)}
                    disabled={searching || syncing}
                  >
                    <Search />
                    Search Pool
                  </LwButton>
                )}
              </div>

              {syncError && (
                <div className="lw-credential-dialog__notice" data-tone="destructive" role="alert">
                  <AlertCircle aria-hidden="true" />
                  <p>{syncError}</p>
                </div>
              )}

              {(searching || syncing) && (
                <div className="lw-credential-dialog__loading" role="status">
                  <Loader2 className="animate-spin" />
                  <span>{syncing ? 'Syncing from Twilio...' : 'Searching pool...'}</span>
                </div>
              )}

              {!searching && !syncing && pooledNumbers.length > 0 && (
                <>
                  <div className="lw-credential-dialog__section-heading">
                    <span>Available numbers ({pooledNumbers.length})</span>
                  </div>
                  <RadioGroup
                    className="lw-credential-dialog__choices"
                    aria-label="Available pooled phone numbers"
                    value={selectedNumberId}
                    onValueChange={(value) => {
                      setSelectedNumberId(value);
                      setSelectedTwilioNumber('');
                    }}
                  >
                    {pooledNumbers.map((number) => {
                      const optionId = `pooled-number-${number.id}`;
                      return (
                        <div
                          key={number.id}
                          className="lw-credential-dialog__choice"
                          data-selected={selectedNumberId === number.id}
                          onClick={() => setSelectedNumberId(number.id)}
                        >
                          <RadioGroupItem value={number.id} id={optionId} />
                          <Label htmlFor={optionId} className="lw-credential-dialog__choice-label">
                            <span className="lw-credential-dialog__choice-copy">
                              <span className="lw-credential-dialog__choice-title lw-credential-dialog__mono">
                                {formatPhoneNumber(number.phoneNumber)}
                              </span>
                              <span className="lw-credential-dialog__choice-meta">
                                {number.locality}, {number.region} · Area Code: {number.areaCode}
                                {number.assignmentCount > 0
                                  ? ` · assigned ${number.assignmentCount}× before`
                                  : ''}
                              </span>
                            </span>
                            <LwStatusPill label="Available" tone="success" />
                          </Label>
                        </div>
                      );
                    })}
                  </RadioGroup>
                </>
              )}

              {!searching && !syncing && pooledNumbers.length === 0 && (
                <div className="lw-credential-dialog__notice" data-tone="neutral">
                  <Info aria-hidden="true" />
                  <p>
                    No numbers found in the pool.{' '}
                    {canSync
                      ? 'Sync from Twilio to import owned numbers, or choose Purchase New Number.'
                      : 'Go back and choose Purchase New Number.'}
                  </p>
                </div>
              )}
            </>
          )}

          {assignmentFlow === 'purchase' && (
            <>
              <button
                type="button"
                className="lw-credential-dialog__back"
                onClick={handleBackToInitial}
              >
                ← Back to Options
              </button>

              <div className="lw-credential-dialog__notice" data-tone="purchase">
                <Info aria-hidden="true" />
                <p>
                  Purchasing a new number costs <strong>$1.15/month</strong> plus message
                  fees ($0.0079 per SMS).
                </p>
              </div>

              <div className="lw-credential-dialog__search lw-credential-dialog__search--twilio">
                <div className="lw-credential-dialog__field">
                  <Label htmlFor="purchaseAreaCode">
                    Area code <span aria-hidden="true">*</span>
                  </Label>
                  <Input
                    id="purchaseAreaCode"
                    placeholder="Required: 303, 720, 818, etc."
                    value={areaCodeFilter}
                    onChange={(event) => setAreaCodeFilter(event.target.value.replace(/\D/g, '').slice(0, 3))}
                    maxLength={3}
                    required
                  />
                  <p>Enter a 3-digit area code to search for available numbers</p>
                </div>
                <LwButton
                  variant="sm-solid"
                  onClick={searchTwilioInventory}
                  disabled={searching || areaCodeFilter.length !== 3}
                >
                  {searching ? <Loader2 className="animate-spin" /> : <Search />}
                  {searching ? 'Searching...' : 'Search Twilio'}
                </LwButton>
              </div>

              {searching && (
                <div className="lw-credential-dialog__loading" role="status">
                  <Loader2 className="animate-spin" />
                  <span>Searching Twilio inventory...</span>
                </div>
              )}

              {!searching && twilioSearchResults.length > 0 && (
                <>
                  <div className="lw-credential-dialog__section-heading">
                    <span>Available for purchase ({twilioSearchResults.length})</span>
                  </div>
                  <RadioGroup
                    className="lw-credential-dialog__choices"
                    aria-label="Available Twilio phone numbers"
                    value={selectedTwilioNumber}
                    onValueChange={setSelectedTwilioNumber}
                  >
                    {twilioSearchResults.map((number) => {
                      const optionId = `twilio-number-${number.phoneNumber.replace(/\D/g, '')}`;
                      return (
                        <div
                          key={number.phoneNumber}
                          className="lw-credential-dialog__choice"
                          data-selected={selectedTwilioNumber === number.phoneNumber}
                          onClick={() => setSelectedTwilioNumber(number.phoneNumber)}
                        >
                          <RadioGroupItem value={number.phoneNumber} id={optionId} />
                          <Label htmlFor={optionId} className="lw-credential-dialog__choice-label">
                            <span className="lw-credential-dialog__choice-copy">
                              <span className="lw-credential-dialog__choice-title lw-credential-dialog__mono">
                                {formatPhoneNumber(number.phoneNumber)}
                              </span>
                              <span className="lw-credential-dialog__choice-meta">
                                {number.locality}, {number.region}
                              </span>
                              <span className="lw-credential-dialog__capabilities">
                                {number.numberType && <span>{number.numberType}</span>}
                                {number.capabilities.sms && <span>SMS</span>}
                                {number.capabilities.mms && <span>MMS</span>}
                              </span>
                            </span>
                            <LwStatusPill
                              label="$1.15/mo"
                              tone="info"
                              className="lw-credential-dialog__price-pill"
                            />
                          </Label>
                        </div>
                      );
                    })}
                  </RadioGroup>
                </>
              )}

              {!searching && twilioSearchResults.length === 0 && (
                <div className="lw-credential-dialog__notice" data-tone="neutral">
                  <Info aria-hidden="true" />
                  <p>
                    No search results yet. Enter a valid 3-digit area code and select
                    Search Twilio to find available numbers.
                  </p>
                </div>
              )}
            </>
          )}
        </LwModal>
      )}

      {unlinkDialogOpen && (
        <LwModal
          className="lw-credential-dialog lw-credential-dialog--destructive"
          containerClassName="lw-credential-modal"
          size="wide"
          title={<span className="lw-credential-dialog__danger-title">Unassign SMS Number</span>}
          sub="Are you sure you want to unassign this phone number? It will be returned to the pool for reuse by other users."
          onClose={() => setUnlinkDialogOpen(false)}
          footer={(
            <>
              <LwButton
                variant="sm"
                onClick={() => setUnlinkDialogOpen(false)}
                disabled={assigning}
              >
                Cancel
              </LwButton>
              <LwButton
                variant="sm-danger"
                onClick={handleUnassignNumber}
                disabled={assigning}
              >
                {assigning ? (
                  <>
                    <Loader2 className="animate-spin" />
                    Unassigning...
                  </>
                ) : (
                  <>
                    <Unlink />
                    Unassign Number
                  </>
                )}
              </LwButton>
            </>
          )}
        >
          <div className="lw-credential-dialog__notice" data-tone="warning">
            <AlertCircle aria-hidden="true" />
            <p>
              <strong>Note:</strong> The number will not be deleted from Twilio. It will
              be returned to the pool and can be reassigned to another user.
            </p>
          </div>
          {smsNumber && (
            <div className="lw-credential-dialog__summary">
              <span>Phone Number</span>
              <code>{formatPhoneNumber(smsNumber)}</code>
            </div>
          )}
        </LwModal>
      )}
    </>
  );
};
